import { useEffect, useRef, useState } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import 'leaflet-rotate'
import { initializeMapLayers } from './MapLayers'

/**
 * Interactive map for placing treasures in admin panel
 * @param {Array} treasures - Current treasures list
 * @param {Object} newTreasure - Currently editing treasure coords
 * @param {Function} onSetCoords - Callback when coordinates selected
 */
export default function AdminMap({ treasures, newTreasure, onSetCoords }) {
  const mapRef = useRef(null)
  const mapInstance = useRef(null)
  const editMarker = useRef(null)
  const treasureMarkers = useRef({})
  const [centerCoords, setCenterCoords] = useState(null)

  useEffect(() => {
    // Initialize map
    if (!mapInstance.current && mapRef.current) {
      const mapOptions = {}
      // Only enable rotation if the leaflet-rotate plugin is available
      if (L.Map.prototype.rotate) {
        mapOptions.rotate = true
      }

      mapInstance.current = L.map(mapRef.current, mapOptions).setView([51.505, -0.09], 13)

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
        maxZoom: 24
      }).addTo(mapInstance.current)

      // Initialize backyard overlay
      initializeMapLayers(mapInstance.current)

      // Update center coordinates continuously during drag and on move
      const updateCenter = () => {
        const center = mapInstance.current.getCenter()
        // Use full precision available from Leaflet
        setCenterCoords({
          lat: Number(center.lat.toFixed(8)),
          lng: Number(center.lng.toFixed(8))
        })
      }

      // Update on every move (pan/zoom)
      mapInstance.current.on('move', updateCenter)

      // Also update during drag for continuous feedback
      mapInstance.current.on('drag', updateCenter)

      // Initial update
      updateCenter()

      // Disable click-to-place, use drag instead
      mapInstance.current.dragging.enable()
    }
  }, [])

  // Update treasure markers when treasures list changes
  useEffect(() => {
    if (!mapInstance.current) return

    // Clear old markers
    Object.values(treasureMarkers.current).forEach(marker => {
      mapInstance.current.removeLayer(marker)
    })
    treasureMarkers.current = {}

    // Add treasure markers
    treasures.forEach((t, idx) => {
      const icon = L.divIcon({
        html: `<div style="background: #FFD700; width: 30px; height: 30px; border-radius: 50%; border: 3px solid white; display: flex; align-items: center; justify-content: center; font-weight: bold; font-size: 12px;">${idx + 1}</div>`,
        iconSize: [30, 30]
      })

      const marker = L.marker([t.lat, t.lng], { icon }).addTo(mapInstance.current)
      marker.bindPopup(`<strong>${t.keyword}</strong>`)
      treasureMarkers.current[t.id] = marker
    })

    // Fit bounds if treasures exist
    if (treasures.length > 0) {
      const bounds = treasures.map(t => [t.lat, t.lng])
      mapInstance.current.fitBounds(bounds, { padding: [50, 50] })
    }
  }, [treasures])

  // Update edit marker when newTreasure coords change
  useEffect(() => {
    if (!mapInstance.current) return

    if (editMarker.current) {
      mapInstance.current.removeLayer(editMarker.current)
    }

    if (newTreasure.lat && newTreasure.lng) {
      const lat = parseFloat(newTreasure.lat)
      const lng = parseFloat(newTreasure.lng)

      if (!isNaN(lat) && !isNaN(lng)) {
        const icon = L.divIcon({
          html: '<div style="background: #FF6B6B; width: 30px; height: 30px; border-radius: 50%; border: 3px solid white; display: flex; align-items: center; justify-content: center;">✏️</div>',
          iconSize: [30, 30]
        })

        editMarker.current = L.marker([lat, lng], { icon }).addTo(mapInstance.current)
        mapInstance.current.setView([lat, lng], 15)
      }
    }
  }, [newTreasure.lat, newTreasure.lng])

  return (
    <div style={styles.container}>
      <div style={styles.mapWrapper}>
        <div style={styles.map} ref={mapRef} />
        {/* Crosshair overlay */}
        <div style={styles.crosshair}>
          <div style={styles.crosshairH} />
          <div style={styles.crosshairV} />
        </div>
        {/* Center coordinates display */}
        {centerCoords && (
          <div style={styles.coordsDisplay}>
            {centerCoords.lat.toFixed(8)}, {centerCoords.lng.toFixed(8)}
          </div>
        )}
      </div>
      <div style={styles.controls}>
        <button
          onClick={() => {
            if (centerCoords) {
              onSetCoords(centerCoords.lat, centerCoords.lng)
            }
          }}
          style={styles.confirmBtn}
        >
          ✓ Confirm Position
        </button>
        <p style={styles.hint}>Drag map to position crosshair • Click Confirm to set coordinates</p>
      </div>
    </div>
  )
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    padding: '20px',
    background: '#f9f9f9',
    borderRadius: '8px'
  },
  mapWrapper: {
    flex: 1,
    position: 'relative',
    borderRadius: '4px',
    border: '1px solid #ddd',
    minHeight: '400px',
    overflow: 'hidden'
  },
  map: {
    width: '100%',
    height: '100%'
  },
  crosshair: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '40px',
    height: '40px',
    pointerEvents: 'none',
    zIndex: 1000
  },
  crosshairH: {
    position: 'absolute',
    top: '50%',
    left: 0,
    width: '40px',
    height: '2px',
    background: '#FF6B6B',
    transform: 'translateY(-50%)'
  },
  crosshairV: {
    position: 'absolute',
    top: 0,
    left: '50%',
    width: '2px',
    height: '40px',
    background: '#FF6B6B',
    transform: 'translateX(-50%)'
  },
  coordsDisplay: {
    position: 'absolute',
    bottom: '15px',
    left: '15px',
    background: 'rgba(0, 0, 0, 0.7)',
    color: '#fff',
    padding: '8px 12px',
    borderRadius: '4px',
    fontSize: '11px',
    fontFamily: 'monospace',
    zIndex: 999,
    pointerEvents: 'none'
  },
  controls: {
    marginTop: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  confirmBtn: {
    padding: '10px 16px',
    background: '#28A745',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'background 0.2s'
  },
  hint: {
    fontSize: '11px',
    color: '#666',
    marginTop: 0,
    marginBottom: 0
  }
}
